import { useEffect, useRef, useState } from "react";
import { Label, TextInput } from "flowbite-react";
import "../registerLogin/RegisterlLogin.css";
import { useFormik } from "formik";
import { boolean, object, string } from "yup";
import { checkPseudoAlreadyExists, registerUser } from "../../services/callAPI/toRegisterUser";
import UnvalidToast from "../toasts/UnvalidToast";
import CheckEmailToast from "../toasts/CheckEmailToast";
import { regions } from "../../services/utils/regions/regions";
import { noInvisibleChars } from "../../services/hooks/sanitizeValue/noInvisibleChar";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

export function FormRegister() {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isShowInputRegion, setIsShowInputRegion] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<{ code: string; nom: string }[]>([]);
  const [showUl, setShowUl] = useState<boolean>(false);
  const [isShowCheckEmailToast, setIsShowCheckEmailToast] = useState<boolean>(false);
  const [isShowUnValidToast, setIsShowUnValidToast] = useState<boolean>(false);
  const regionRef = useRef<HTMLDivElement>(null);

  function normalize(str: string) {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  }

  // function to handle region input
  function handleRegionInput(e: React.ChangeEvent<HTMLInputElement>) {
    const inputValue = normalize(e.target.value);

    if (inputValue.trim() === "") {
      setSuggestions([]);
      setShowUl(false);
      return;
    }

    const sugges = regions.filter((region) =>
      normalize(region.nom).startsWith(inputValue)
    );

    setSuggestions(sugges);
    setShowUl(true);
  }

  //set validation schema with Yup
  const registerSchema = object({
    role: string().oneOf(["TRAVELER", "RESIDENT"], "rôle inconnu").required("Vous êtes un voyageur ou un résident"),
    email: string()
      .trim()
      .email(`Doit être un email valide`)
      .min(5, "L'email doit comporter au moins 5 caractères")
      .max(254, "L'email ne peut pas dépasser 254 caractères")
      .test("is-email-valid", "l'email contient des caractères non-autorisé", (value) => noInvisibleChars(value))
      .matches(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
        "Le domaine de l'email doit être valide")
      .matches(
        /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,  // Expression régulière pour filtrer les domaines invalides
        "Le domaine de l'email doit être valide"
      )
      .test(//check if domain name is valid to way strict
        "is-valid-domain",
        "Le domaine de l'email est invalide",
        (value) => {
          if (!value) return false;
          const domainPattern = /^[^\s@]+@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
          return domainPattern.test(value);
        }
      )
      .required(`Veuillez renseigner votre adresse mail`),
    pseudo: string()
      .transform(value => value?.trim())
      .min(3, `Doit avoir 3 caractères minimum`)
      .max(30, `Doit avoir 30 caractères maximum`)
      .test(
        "no-whitespace-inside",
        "Le pseudo ne doit pas contenir d'espaces",
        (value) => !/\s/.test(value ?? "")
      )
      .matches(/^(?![_.])(?!.*[_.]{2})[a-zA-Z0-9._]+(?<![_.])$/, 'Le pseudo ne doit contenir que des lettres, chiffres, underscores (_), sans commencer ou finir par . ou _ et sans doublons consécutifs.')
      .test(//check if pseudo exist in async
        "is-pseudo-unique",
        "essayer un autre pseudo",
        async (value) => {
          if (!value) return false; // check if pseudo is empty
          const isExist = await checkPseudoAlreadyExists(value); // check if pseudo exists also
          return !isExist;
        }
      )
      .required("Pseudo requis"),
    region: string().nullable().min(5, "5 caractères minimum").max(26, "26 caractères maximum")
      .when('role', (role, schema) => {
        const roleValue = Array.isArray(role) ? role[0] : role;
        if (roleValue === 'RESIDENT') {
          const regionNames = regions?.map(region => region.nom) || []; //extract region name to use oneOf into Yup
          return schema
            .required("Indiquez votre région")
            .oneOf(regionNames, "La région sélectionnée n'est pas valide")
        }
        return schema; // Si le rôle n'est pas 'resident', le champ 'region' reste facultatif.
      }),
    password: string()
      .trim()
      .required("Mot de passe requis")
      .min(8, "8 caractères minimum")
      .max(64, "64 caractères maximum")
      .matches(/[A-Z]/, "Le mot de passe doit contenir au moins une majuscule")
      .matches(/[a-z]/, "Le mot de passe doit contenir au moins une minuscule")
      .matches(/[0-9]/, "Le mot de passe doit contenir au moins un chiffre")
      .matches(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/, "Le mot de passe doit contenir au moins un caractère spécial")
      .test("no-invisible-chars", "Le mot de passe ne doit pas contenir d'espacce blanc", (value) => noInvisibleChars(value)),
    acceptedCGU: boolean()
      .oneOf([true], 'Vous devez accepter les Conditions Générales d’Utilisation')
      .required("L’acceptation des CGU est requise"),
    acceptedCharterDrive: boolean()
      .oneOf([true], 'Vous devez accepter la charte de bonne conduite')
      .required("L’acceptation de la charte de bonne conduite est requise"),
  })

  // set formik
  const { handleSubmit, handleChange, errors, touched, isSubmitting, setFieldValue, values } = useFormik({
    initialValues: {
      role: "",
      email: "",
      pseudo: "",
      region: "",
      password: "",
      acceptedCGU: false,
      acceptedCharterDrive: false
    },
    validationSchema: registerSchema,
    validateOnChange: false,
    onSubmit: async values => {
      try {
        // capitalization leters first into pseudo
        const trimmed = values.pseudo.trim();
        values.pseudo = trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();

        //call Api
        const isRegister = await registerUser(values);

        if (isRegister) { //success register
          // show toast succes
          setIsShowCheckEmailToast(true);

          // missing toast next 2s
          const waitForToastToDisappear = () => {
            return new Promise<void>((resolve) => {
              setTimeout(() => {
                setIsShowCheckEmailToast(false);
                resolve(); // Résoudre la promesse une fois que le toast est caché
              }, 2000);
            });
          };

          waitForToastToDisappear().then(() => {
            //redirect to connexion page
            location.href = '/';
          })
        }
        else {//failure register
          /* show toast failure*/
          setIsShowUnValidToast(true);

          // missing toast next 3s
          setTimeout(() => {
            setIsShowUnValidToast(false);
          }, 3000);
        }

      } catch (error) {
        //failure register, show toast failure
        setIsShowUnValidToast(true);

        // missing toast next 3s
        setTimeout(() => {
          setIsShowUnValidToast(false);
        }, 3000);
      }
    }
  })

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (regionRef.current && !regionRef.current.contains(event.target as Node)) {
        setShowUl(false); // ferme la liste
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <article className="flex flex-col items-center mx-6">
      {isShowCheckEmailToast && <CheckEmailToast />}
      {isShowUnValidToast && <UnvalidToast />}

      <h2 className="max-w-md title2">Inscription</h2>

      {/* formulaire */}
      <form className="max-w-md last:mb-0" onSubmit={handleSubmit}>

        <fieldset id="fieldset-register" className="flex flex-col gap-4">

          <legend >En tant que</legend>

          {/* je m'inscris en tant */}
          <div className="flex items-center justify-around gap-x-3">

            {/* en tant Voyageur */}
            <div className="flex items-center gap-x-4">
              <TextInput id="Voyageur" name="role" type="radio" value={"TRAVELER"} onChange={handleChange} onClick={() => { setIsShowInputRegion(false); setFieldValue("region", "") }} />
              <div className="mb-2 block text-left">
                <Label className="cursor-pointer" htmlFor="Voyageur" value="Voyageur" />
              </div>
            </div>

            {touched.role && errors.role && <span className="text-red-500 text-sm">{errors.role}</span>}

            {/* en tant que Résident */}
            <div className="flex items-center gap-x-4">
              <TextInput id="Resident" name="role" type="radio" value={"RESIDENT"} onChange={handleChange} onClick={() => setIsShowInputRegion(true)} />
              <div className="mb-2 block text-left">
                <Label className="cursor-pointer" htmlFor="Resident" value="Résident" />
              </div>
            </div>

          </div>

          {/* je rentre l'email */}
          <div>
            <div className="mb-2 block text-left">
              <Label htmlFor="email" value="Email" />
            </div>
            <input autoComplete="email" className="border border-gray-500 rounded-md bg-gray-300 w-full" name="email" id="email" type="email" placeholder="eleonore@hotmail.com" onChange={handleChange} />
            {touched.email && errors.email && <span className="text-red-500 text-sm">{errors.email}</span>}
          </div>

          {/* je rentre mon pseudo*/}
          <div>
            <div className="mb-2 block text-left">
              <Label htmlFor="pseudo" value="Pseudo" />
            </div>
            <input className="border border-gray-500 rounded-md bg-gray-300 w-full" name="pseudo" id="pseudo" type="text" onChange={handleChange} />
            {touched.pseudo && errors.pseudo && <span className="text-red-500 text-sm">{errors.pseudo}</span>}
          </div>

          {/* je rentre ma region*/}
          {isShowInputRegion === true ?
            <div ref={regionRef}>
              <div className="mb-2 block text-left">
                <Label htmlFor="region" value="Région" />
              </div>
              <input
                className="border border-gray-500 rounded-md bg-gray-300 w-full"
                name="region"
                id="region"
                type="text"
                onChange={(e) => { handleRegionInput(e); handleChange(e) }}
                onFocus={() => {
                  // Affiche toutes les régions si le champ est vide
                  setSuggestions(regions);
                  setShowUl(true);
                }}
                value={values.region}
              />
              {showUl && suggestions.length > 0 && (
                <ul className="w-full max-h-16 overflow-y-auto">
                  {suggestions?.map((suggestion, index) => (<li key={index} className="border rounded-md border-gray-500 cursor-pointer hover:bg-gray-200" onClick={() => {
                    setFieldValue("region", suggestion.nom)
                    setShowUl(false);
                  }}>
                    {suggestion.nom}</li>))}
                </ul>
              )}
              {touched.region && errors.region && <span className="text-red-500 text-sm">{errors.region}</span>}
            </div> : null
          }

          {/* je rentre le Mot de passe */}
          <div>
            <div className="mb-2 block text-left">
              <Label htmlFor="password1" value="Mot de passe" />
            </div>
            <div className="relative">
              <input
                autoComplete="new-password"
                className="border border-gray-500 rounded-md bg-gray-300 w-full"
                id="password1"
                name="password"
                type={showPassword ? "text" : "password"}
                onChange={handleChange}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-sm"
              >
                {showPassword ? <FontAwesomeIcon icon={faEyeSlash} /> : <FontAwesomeIcon icon={faEye} />}
              </button>
            </div>
            {touched.password && errors.password && <span className="text-red-500 text-sm">{errors.password}</span>}
          </div>

          {/* accepting cgu */}
          <div>
            <label className="flex items-center space-x-2 mb-3">
              <input
                type="checkbox"
                name="acceptedCGU"
                checked={values.acceptedCGU}
                onChange={handleChange}
                className="w-4 h-4"
              />
              <span>
                J’accepte les{' '}
                <NavLink
                  to={"/terms-services"}
                  className="text-blue-600 underline"
                >
                  Conditions Générales d’Utilisation
                </NavLink>
              </span>
            </label>
            {touched.acceptedCGU && errors.acceptedCGU && <span className="text-red-500 text-sm">{errors.acceptedCGU}</span>}
          </div>

          {/* accepting good drive charter */}
          <div>
            <label className="flex items-center space-x-2 mb-3">
              <input
                type="checkbox"
                name="acceptedCharterDrive"
                checked={values.acceptedCharterDrive}
                onChange={handleChange}
                className="w-4 h-4"
              />
              <span>
                J’accepte la{' '}
                <NavLink
                  to={"/charter-of-good-conduct"}
                  className="text-blue-600 underline"
                >
                  Charte de bonne conduite
                </NavLink>
              </span>
            </label>
            {touched.acceptedCharterDrive && errors.acceptedCharterDrive && <span className="text-red-500 text-sm">{errors.acceptedCharterDrive}</span>}
          </div>

          {/* je soumets */}
          <button style={{ backgroundColor: "#335C67", color: "white" }} className="rounded-2xl py-2 mt-4" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Envoi en cours...' : `S'inscrire`}
          </button>

        </fieldset>

      </form>

    </article>
  );
}
